/**
 * 중앙화된 API Client (모바일 패턴 미러링)
 * - 클래스 기반 싱글톤
 * - setAuthContext()로 Context 참조 주입
 * - Request interceptor: 토큰 자동 첨부
 * - Response interceptor: 에러 표준화 + 401 자동 로그아웃
 */

import axios from 'axios';

// AuthContext 참조를 위한 모듈 레벨 변수
let authContextRef = null;

/**
 * AuthContext 참조를 외부에서 주입하는 함수
 * AuthProvider 마운트 시 호출하여 apiClient가 401 시 자동 로그아웃 가능하게 한다.
 * @param {object} authContext - { handleLogout } 등을 포함하는 AuthContext 값
 */
export const setAuthContext = (authContext) => {
  authContextRef = authContext;
};

class ApiClient {
  constructor() {
    // baseURL 미설정 → CRA proxy 사용 (package.json "proxy": "http://localhost:3001")
    this.client = axios.create({
      headers: { 'Content-Type': 'application/json' },
      timeout: 120000, // 120초 (AI 분석 API는 시간이 오래 걸림)
    });

    this._setupInterceptors();
  }

  _setupInterceptors() {
    // Request interceptor: localStorage에서 토큰 읽어 Authorization 헤더 첨부
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: 에러를 Standard_Error로 변환
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        // 401 Unauthorized → 토큰 제거 + 자동 로그아웃
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          if (authContextRef?.handleLogout) {
            authContextRef.handleLogout();
          }
        }
        throw this._toStandardError(error);
      }
    );
  }

  /**
   * HTTP 에러를 Standard_Error 형태로 변환
   * @param {object} error - axios 에러 객체
   * @returns {{ code: string, message: string, userMessage: string, retriable: boolean, details?: object }}
   */
  _toStandardError(error) {
    const response = error.response;

    // 네트워크 에러 (서버 응답 없음)
    if (!response) {
      return {
        code: 'NETWORK_ERROR',
        message: error.message || 'Network Error',
        userMessage: '네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.',
        retriable: true,
      };
    }

    const status = response.status;
    const serverMessage = response.data?.message || response.statusText || error.message;

    if (status === 401) {
      return {
        code: 'UNAUTHORIZED',
        message: serverMessage,
        userMessage: '인증이 만료되었습니다. 다시 로그인해주세요.',
        retriable: false,
      };
    }

    if (status === 403) {
      return {
        code: 'FORBIDDEN',
        message: serverMessage,
        userMessage: '접근 권한이 없습니다.',
        retriable: false,
      };
    }

    if (status === 404) {
      return {
        code: 'NOT_FOUND',
        message: serverMessage,
        userMessage: '요청한 리소스를 찾을 수 없습니다.',
        retriable: false,
      };
    }

    if (status === 429) {
      return {
        code: 'RATE_LIMITED',
        message: serverMessage,
        userMessage: '너무 많은 요청이 있었습니다. 잠시 후 다시 시도해주세요.',
        retriable: true,
      };
    }

    if (status >= 500) {
      return {
        code: 'SERVER_ERROR',
        message: serverMessage,
        userMessage: '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
        retriable: true,
        details: response.data,
      };
    }

    // 기타 4xx 에러
    return {
      code: `HTTP_${status}`,
      message: serverMessage,
      userMessage: serverMessage || '요청을 처리할 수 없습니다.',
      retriable: false,
      details: response.data,
    };
  }

  /** @param {string} url @param {object} [config] @returns {Promise<any>} */
  async get(url, config = {}) {
    const response = await this.client.get(url, config);
    return response.data;
  }

  /** @param {string} url @param {object} [data] @param {object} [config] @returns {Promise<any>} */
  async post(url, data = {}, config = {}) {
    const response = await this.client.post(url, data, config);
    return response.data;
  }

  /** @param {string} url @param {object} [data] @param {object} [config] @returns {Promise<any>} */
  async put(url, data = {}, config = {}) {
    const response = await this.client.put(url, data, config);
    return response.data;
  }

  /** @param {string} url @param {object} [data] @param {object} [config] @returns {Promise<any>} */
  async patch(url, data = {}, config = {}) {
    const response = await this.client.patch(url, data, config);
    return response.data;
  }

  /** @param {string} url @param {object} [config] @returns {Promise<any>} */
  async delete(url, config = {}) {
    const response = await this.client.delete(url, config);
    return response.data;
  }
}

// Singleton instance
export const apiClient = new ApiClient();

// Default export for backward compatibility
export default apiClient;
